import { Fragment, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns'
// @mui
import {
  Typography,
  Stack,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  CardActionArea,
  Radio,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
// form
import { useForm } from 'react-hook-form';
// components
import DIOText from '../../components/DIOText';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import { FormProvider } from '../../components/hook-form';
import { TextField } from '@mui/material';
import ContractDateRangeDialog from './ContractDateRangeDialog';
import {
  NegotiateSuggestAmendBeforeRejectDialog,
  ContractInfoDialog,
  ContractDoneDialog,
}
from './negotiate';
import {
  NegoSelectPlanWarnDialog,
} from './';
import RejectCrewDialog from './RejectCrewDialog';
import { InvitationNudgeSnackbar } from '../invitation';
import { CoreTimePopover } from '../timetracking';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import useCompany from '../../hooks/useCompany';
// util
import axios from '../../utils/axios';
import { fKrDateKST, fKrDateAbsoulte } from '../../utils/formatTime';
import { calculateDiscountPrice } from '../../utils/calculateDiscount';
// constants
import { NORMAL_FEE_RATE, TRIAL_FEE_RATE, LONGTERM_FEE_RATE, RESIENDT_FEE_RATE } from '../../constants';

const FormStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.neutral,
}));

const InfoStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(1),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.background.neutral,
}));

const CaptionSecondaryText = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));


export default function NegotiateCard({ jobPost, candidate, loadData }) {
  const { user } = useAuth();
  const { getCrews } = useCompany();
  const [isSubmit, setIsSubmit] = useState(false);
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();
  // for feedback
  const [writeNegoComment, setWriteNegoComment] = useState(false);
  const [negoCommentError, setNegoCommentError] = useState(false);
  const [negoComment, setNegoComment] = useState('');
  // for accept
  const [acceptNego, setAcceptNego] = useState(false);
  const [contractDoneDialog, setContractDoneDialog] = useState(false);
  const [depositGuide, setDepositGuide] = useState({});
  // for reject
  const [bizRejectDialog, setBizRejectDialog] = useState(false);
  // for contract date range
  const [contractDateRange, setContractDateRange] = useState(false);
  const [contractDateRangeValue, setContractDateRangeValue] = useState([null, null]);
  const [contractTrialDateRangeValue, setContractTrialDateRangeValue] = useState([null, null]);
  const [contractLongtermDateRangeValue, setContractLongtermDateRangeValue] = useState([null, null]);
  const [hasContractRange, setHasContractRange] = useState(false);
  // for suggest feedback not reject
  const [suggestFeedback, setSuggestFeedback] = useState(false);
  // for update contract info
  const [contractInfoDialog, setContractInfoDialog] = useState(false);
  // nudge snackbar
  const [isOpenNudge, setIsOpenNudge] = useState(true);

  const allowLongTerm = (
    typeof candidate?.longterm_salary === 'number'
    && candidate?.longterm_salary > 0
    && typeof candidate?.longterm_working_hours_per_week === 'number'
    && candidate?.longterm_working_hours_per_week > 0
  );
  const isResidentWork = candidate?.work_type === 'resident';

  // discount
  const salary = parseInt(candidate?.nego_salary);
  const feeRate = parseInt(candidate?.fee_rate) === 0 ? 0 : isResidentWork ? RESIENDT_FEE_RATE : parseInt(candidate?.fee_rate);
  const {
    fee=0,
    vat=0,
    total=0,
    finSalary=0,
    finFee=0,
    finVat=0,
    finTotal=0,
    dcAmount=0,
    feeDcAmount=0,
  } = calculateDiscountPrice(
    salary,
    feeRate,
    0,
    candidate?.available_discount_amount
  );

  const trialSalary = parseInt(candidate?.nego_trial_salary);
  const trialFeeRate = parseInt(candidate?.fee_rate) === 0 ? 0 : isResidentWork ? RESIENDT_FEE_RATE : allowLongTerm ? TRIAL_FEE_RATE : parseInt(candidate?.fee_rate);
  const {
    fee: trialFee=0,
    vat: trialVat=0,
    total: trialTotal=0,
    finSalary: trialFinSalary=0,
    finFee: trialFinFee=0,
    finVat: trialFinVat=0,
    finTotal: trialFinTotal=0,
    dcAmount: trialDcAmount=0,
    feeDcAmount: trialFeeDcAmount=0,
  } = calculateDiscountPrice(
    trialSalary,
    trialFeeRate,
    0,
    candidate?.available_discount_amount
  );

  const longtermSalary = parseInt(candidate?.nego_longterm_salary);
  const longtermFeeRate = parseInt(candidate?.fee_rate) === 0 ? 0 : isResidentWork ? RESIENDT_FEE_RATE : allowLongTerm ? LONGTERM_FEE_RATE : parseInt(candidate?.fee_rate);
  const {
    fee: longtermFee=0,
    vat: longtermVat=0,
    total: longtermTotal=0,
    finSalary: longtermFinSalary=0,
    finFee: longtermFinFee=0,
    finVat: longtermFinVat=0,
    finTotal: longtermFinTotal=0,
    dcAmount: longtermDcAmount=0,
    feeDcAmount: longtermFeeDcAmount=0,
  } = calculateDiscountPrice(
    longtermSalary,
    longtermFeeRate,
    0,
    candidate?.available_discount_amount
  );

  // plan
  const [plan, setPlan] = useState(null);
  const [is4weeksExpanded, setIs4weeksExpanded] = useState(false);
  const [isTrialExpanded, setIsTrialExpanded] = useState(false);
  const [isLongtermExpanded, setIsLongtermExpanded] = useState(false);
  const [isOpenPlanWarnDialog, setIsOpenPlanWarnDialog] = useState(false);
  const planSelected = candidate.status === 'accept';
  const plan4weeksSelected = planSelected && !candidate?.nego_trial_selected && !candidate?.nego_longterm_selected;
  const planTrialSelected = planSelected && candidate?.nego_trial_selected;
  const planLongtermSelected = planSelected && candidate?.nego_longterm_selected;

  const select4weeks = () => {
    if (candidate.status !== 'crew-negotiate-to-company') return ;
    setPlan('4weeks');
    setIs4weeksExpanded(true);
  };

  const selectTrial = () => {
    if (candidate.status !== 'crew-negotiate-to-company') return ;
    setPlan('trial');
    setIsTrialExpanded(true);
  };

  const selectLongterm = () => {
    if (candidate.status !== 'crew-negotiate-to-company') return ;
    setPlan('longterm');
    setIsLongtermExpanded(true);
  };

  const toggleExpand4weeks = (e) => {
    e.stopPropagation();
    setIs4weeksExpanded(!is4weeksExpanded);
  };

  const toggleExpandTrial = (e) => {
    e.stopPropagation();
    setIsTrialExpanded(!isTrialExpanded);
  };

  const toggleExpandLongterm = (e) => {
    e.stopPropagation();
    setIsLongtermExpanded((prev) => !prev);
  };

  const handlePlan = (e) => {
    if (candidate.status !== 'crew-negotiate-to-company') return ;
    setPlan(e.target.value);
    if (e.target.value === '4weeks') setIs4weeksExpanded(true);
    if (e.target.value === 'trial') setIsTrialExpanded(true);
    if (e.target.value === 'longterm') setIsLongtermExpanded(true);
  };

  useEffect(() => {
  }, []);

  const defaultValues = {
    reason: [],
    reasonEtc: '',
    feedback: '',
  };

  const methods = useForm({
    defaultValues,
  });

  const closeNudge = async () => {
    setIsOpenNudge(false);
  };

  const closeBizRejectDialog = async () => {
    setBizRejectDialog(false);
  };

  const openBizRejectDialog = async () => {
    setBizRejectDialog(true);
  };

  const openContractDateRangeDialog = async () => {
    if (!plan) {
      setIsOpenPlanWarnDialog(true);
      return;
    }
    setContractDateRange(true);
  };

  const closeContractDateRangeDialog = async (v, w, x) => {
    setContractDateRange(false);
    if (v && v[0] && v[1]) {
      setContractDateRangeValue(v);
      setContractTrialDateRangeValue(w);
      setContractLongtermDateRangeValue(x);
      setHasContractRange(v && v.length > 1 && v[0] && v[1]);
    }
  };

  const openNegoCommentDialog = async () => {
    setNegoCommentError(false);
    setWriteNegoComment(true);
  };

  const closeNegoCommentDialog = async () => {
    setNegoCommentError(false);
    setWriteNegoComment(false);
  };

  const openNegoAcceptDialog = async () => {
    setIsSubmit(true);
    try {
      const companyId = user?.employee.company[0].id;
      // 회사가 계약 진행할 수 있는지 체크
      const canContractResponse = await axios.post(
        `api/company/${companyId}/can-contract/`
      );
      if (canContractResponse?.status === 200) {
        setContractInfoDialog(false);
        setAcceptNego(true);
      } else {
        enqueueSnackbar('정보 확인에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
      }
    } catch (err) {
      if (err?.response?.status === 422) {
        setContractInfoDialog(true);
      } else {
        console.error(err);
      }
    } finally {
      setIsSubmit(false);
    }
  };

  const closeContractInfoDialog = async () => {
    setContractInfoDialog(false);
  }

  const updateAndAcceptNego = async () => {
    openNegoAcceptDialog();
  }

  const closeNegoAcceptDialog = async () => {
    setAcceptNego(false);
  };

  const openContractDoneDialog = async () => {
    setContractDoneDialog(true);
  };

  const closeContractDoneDialog = async () => {
    setContractDoneDialog(false);
    loadData();
  };

  const handleNegoCommentChange = (e) => {
    setNegoCommentError(!e.target.value);
    setNegoComment(e.target.value);
  };

  const negoFeedback = async () => {
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    const candidateId = candidate?.id;
    if (!negoComment) {
      setNegoCommentError(true);
      return;
    }
    try {
      setIsSubmit(true);
      await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/feedback/`,
        { nego_comment: negoComment }
      );
      enqueueSnackbar('수정 요청을 보냈습니다');
      loadData();
      closeNegoCommentDialog();
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      console.error(error);
      enqueueSnackbar('수정 요청에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };
  
  const negoConfirm = async () => {
    if (!hasContractRange) {
      enqueueSnackbar('계약 시작일자를 선택해주세요', { variant: 'info' });
      return ;
    }
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    const candidateId = candidate?.id;
    try {
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/negotiate-conclude/`,
        {
          confirmed_start_date: format(contractDateRangeValue[0], 'yyyy-MM-dd'),
          plan,
        }
      );
      closeNegoAcceptDialog();
      setDepositGuide(response?.data);
      openContractDoneDialog();
      getCrews();
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      console.error(error);
      enqueueSnackbar('계약 진행 요청에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  const rejectOffer = async (data) => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    const candidateId = candidate?.id;
    try {
      const passData = {
        reject_reason: data.reason,
        matching_feedback: data.feedback,
      };
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/pass/`,
        passData
      );
      setIsSubmit(false);
      loadData();
      closeBizRejectDialog();
      enqueueSnackbar('계약을 거절하셨습니다');
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar('계약 거절에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    }
  }

  return (
    <>
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        border: '1px solid',
        '&:hover': {
          boxShadow: (theme) => theme.customShadows.z24,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {`${candidate.status === "accept" ? "계약 조건" : `${candidate.crew_nickname} 크루의 제안 조건`}`}
        </Typography>

        {candidate.status !== 'accept' && (
          <Stack sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "secondary.main" }} />
              <Typography variant="h6">
                크루의 업무 시작 가능일
              </Typography>
            </Stack>
            <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
              {fKrDateAbsoulte(candidate.min_start_date)} 이후
            </Typography>
          </Stack>
        )}

        {candidate.nego_contact_available_time && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-chat-bubble-dots-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              소통 가능 시간
            </Typography>
          </Stack>
          <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
            {candidate.nego_contact_available_time}
          </Typography>
        </Stack>
        )}

        {candidate.nego_core_time && Array.isArray(candidate.nego_core_time) && candidate.nego_core_time.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-chat-bubble-dots-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              코어 타임
            </Typography>
            <CoreTimePopover />
          </Stack>
          <Stack>
          {candidate.nego_core_time?.map((t, index) => (
            <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1" key={index}>
              {t.day} {t.start.label} ~ {t.end.label}
            </Typography>
          ))}
          </Stack>
        </Stack>
        )}

        <Stack spacing={1} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              업무 내용
            </Typography>
          </Stack>
          <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
            {candidate.nego_task}
          </Typography>
        </Stack>

        {candidate.nego_comment && candidate.status !== 'accept' && (
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "secondary.main" }} />
              <Typography variant="h6">
                {user?.employee.company[0].name}의 {candidate.status === "crew-negotiate-to-company" ? "이전 " : ""}수정 요청사항
              </Typography>
            </Stack>
            <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
              {candidate.nego_comment}
            </Typography>
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack sx={{ mb: 3 }} spacing={1}>
          <Stack sx={{ mb: 2 }} spacing={.5}>
            <Typography variant="h6">
              계약 플랜 {!planSelected && ' 선택'}
            </Typography>
            <Typography>
              {!planSelected && isResidentWork && ('두 가지 계약 플랜 중 원하는 플랜을 선택해주세요.')}
              {!planSelected && !isResidentWork && ('세 가지 계약 플랜 중 원하는 플랜을 선택해주세요.')}
              {plan4weeksSelected && ('4주 단위 계약 플랜을 선택했습니다. 계약은 4주마다 연장하며 진행합니다.')}
              {planTrialSelected && ('2주 단기 체험판 플랜을 선택했습니다. 2주간 크루와 먼저 일해보고 계약을 계속 진행할지 결정하게 됩니다.')}
              {planLongtermSelected && ('8주 장기 계약 플랜을 선택했습니다. 이후 4주 단위로 계약을 연장하며 진행합니다.')}
            </Typography>
          </Stack>
          <Stack sx={{ width: 1 }} direction={{ xs: "column", sm: "column", db: "row" }} alignItems="stretch" spacing={2}>
            {/* 4주 플랜 */}
            <Card
              sx={{
                width: 1,
                borderRadius: 2,
                border: "2px solid rgba(145, 158, 171, 0.24)",
                ...(plan === "4weeks" || plan4weeksSelected) && {
                  backgroundColor: "grey.200",
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
                ...candidate.status !== 'crew-negotiate-to-company' && {
                  boxShadow: "none",
                },
              }}
            >
              <CardActionArea sx={{ p: 3 }} onClick={() => select4weeks()} disabled={planSelected}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">
                      4주 정식 계약
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                      플랜
                    </Typography>
                  </Stack>
                  <Radio color="secondary" value="4weeks" checked={plan==="4weeks" || plan4weeksSelected} onChange={(e) => handlePlan(e)} />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h4" color={"secondary.main"}>{(salary/10000).toLocaleString()}만원</Typography>
                  <Typography variant="h6" color={"secondary.main"}>/ 4주</Typography>
                </Stack>

                <Stack direction="row" spacing={.5} alignItems="center" sx={{ mb: 1 }}>
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b>주 {candidate.nego_working_hours_per_week}시간</b> 근무
                  </Typography>
                </Stack>

                <Collapse
                  in={smUp || is4weeksExpanded}
                  {...(is4weeksExpanded ? { timeout: 500 } : {})}
                >
                <Typography variant="body2">
                  기업에서 가장 많이 선택하는 플랜이에요. <br/>
                  즉시 업무 착수가 필요한 경우 4주 플랜으로 정식 계약을 진행해보세요.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                    <Typography variant="h6">
                      계약 금액
                    </Typography>
                  </Stack>
                  <Stack sx={{ pl: 3 }} spacing={1}>
                    <Stack direction="row" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                      <Typography variant="subtitle2">
                        총 금액
                      </Typography>
                      <Typography variant="subtitle1">
                        {finTotal.toLocaleString()}원
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        크루 인건비
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                        {salary.toLocaleString()}원
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                        수수료 ({feeRate}%)
                      </Typography>
                      <Typography variant="subtitle2">
                        {fee.toLocaleString()}원
                      </Typography>
                    </Stack>
                    {feeDcAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        첫달 수수료할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                      </Typography>
                      <Typography variant="body2">
                        - {feeDcAmount.toLocaleString()}원
                      </Typography>
                    </Stack>
                    )}
                    {dcAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        쿠폰할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                      </Typography>
                      <Typography variant="body2">
                        - {dcAmount.toLocaleString()}원
                      </Typography>
                    </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        부가세
                      </Typography>
                      <Typography variant="body2">
                        {finVat.toLocaleString()}원
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
                </Collapse>
              </CardActionArea>

              {/* collapse button */}
              {!smUp && (
                <Button fullWidth onClick={toggleExpand4weeks} size="small"
                  sx={{ mb: 2, color: "text.disabled" }}
                  endIcon={<Iconify icon={is4weeksExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                >
                  {is4weeksExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                </Button>
              )}
            </Card>

            {/* 2주 trial */}
            {!isResidentWork && (
            <Card
              sx={{
                width: 1,
                borderRadius: 2,
                border: "2px solid rgba(145, 158, 171, 0.24)",
                ...(plan === "trial" || planTrialSelected) && {
                  backgroundColor: "grey.200",
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
                ...candidate.status !== 'crew-negotiate-to-company' && {
                  boxShadow: "none",
                }
              }}
            >
              <CardActionArea sx={{ p: 3 }} onClick={() => selectTrial()} disabled={planSelected}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">
                      2주 단기 체험판
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                      플랜
                    </Typography>
                  </Stack>
                  <Radio color="secondary" value="trial" checked={plan==="trial" || planTrialSelected} onChange={(e) => handlePlan(e)} />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h4" color={"secondary.main"}>{(trialSalary/10000).toLocaleString()}만원</Typography>
                  <Typography variant="h6" color={"secondary.main"}>/ 2주</Typography>
                </Stack>

                <Stack direction="row" spacing={.5} alignItems="center" sx={{ mb: 1 }}>
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b>주 {candidate.nego_trial_working_hours_per_week}시간</b> 근무
                  </Typography>
                </Stack>

                <Collapse
                  in={smUp || isTrialExpanded}
                  {...(isTrialExpanded ? { timeout: 500 } : {})}
                >
                <Typography variant="body2">
                  크루의 작업 방식, 속도를 검증하고 시작하고 싶은 기업이 선택하는 플랜이에요. 2주 동안만 크루와 일해보고, 정식 계약을 결정할 수 있어요.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                    <Typography variant="h6">
                      계약 금액
                    </Typography>
                  </Stack>
                  <Stack sx={{ pl: 3 }} spacing={1}>
                    <Stack direction="row" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                      <Typography variant="subtitle2">
                        총 금액
                      </Typography>
                      <Typography variant="subtitle1">
                        {trialFinTotal.toLocaleString()}원
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        크루 인건비
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                        {trialSalary.toLocaleString()}원
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                        수수료 ({trialFeeRate}%)
                      </Typography>
                      <Typography variant="subtitle2">
                        {trialFee.toLocaleString()}원
                      </Typography>
                    </Stack>
                    {trialFeeDcAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        첫달 수수료할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                      </Typography>
                      <Typography variant="body2">
                        - {trialFeeDcAmount.toLocaleString()}원
                      </Typography>
                    </Stack>
                    )}
                    {trialDcAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        쿠폰할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                      </Typography>
                      <Typography variant="body2">
                        - {trialDcAmount.toLocaleString()}원
                      </Typography>
                    </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        부가세
                      </Typography>
                      <Typography variant="body2">
                        {trialFinVat.toLocaleString()}원
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
                </Collapse>
              </CardActionArea>

              {/* collapse button */}
              {!smUp && (
                <Button fullWidth onClick={toggleExpandTrial} size="small"
                  sx={{ mb: 2, color: "text.disabled" }}
                  endIcon={<Iconify icon={isTrialExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                >
                  {isTrialExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                </Button>
              )}
            </Card>
            )}

            {/* 8주 longterm */}
            <Card
              sx={{
                width: 1,
                borderRadius: 2,
                border: "2px solid rgba(145, 158, 171, 0.24)",
                ...(plan === "longterm" || planLongtermSelected) && {
                  backgroundColor: "grey.200",
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
                ...candidate.status !== 'crew-negotiate-to-company' && {
                  boxShadow: "none",
                }
              }}
            >
              <CardActionArea sx={{ p: 3 }} onClick={() => selectLongterm()} disabled={planSelected}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">
                      8주 장기 계약
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                      플랜
                    </Typography>
                  </Stack>
                  <Radio color="secondary" value="longterm" checked={plan==="longterm" || planLongtermSelected} onChange={(e) => handlePlan(e)} />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h4" color={"secondary.main"}>{(longtermSalary/10000).toLocaleString()}만원</Typography>
                  <Typography variant="h6" color={"secondary.main"}>/ 8주</Typography>
                </Stack>

                <Stack direction="row" spacing={.5} alignItems="center" sx={{ mb: 1 }}>
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b>주 {candidate.nego_longterm_working_hours_per_week}시간</b> 근무
                  </Typography>
                </Stack>

                <Collapse
                  in={smUp || isLongtermExpanded}
                  {...(isLongtermExpanded ? { timeout: 500 } : {})}
                >
                <Typography variant="body2">
                  최소 2달의 근속이 필요한 경우, 장기로 계약할 수 있어요. 필요 시 8주 이상의 기간도 협의해드립니다.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                    <Typography variant="h6">
                      계약 금액
                    </Typography>
                  </Stack>
                  <Stack sx={{ pl: 3 }} spacing={1}>
                    <Stack direction="row" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                      <Typography variant="subtitle2">
                        총 금액
                      </Typography>
                      <Typography variant="subtitle1">
                        {longtermFinTotal.toLocaleString()}원
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        크루 인건비
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                        {longtermSalary.toLocaleString()}원
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                        수수료 ({longtermFeeRate}%)
                      </Typography>
                      <Typography variant="subtitle2">
                        {longtermFee.toLocaleString()}원
                      </Typography>
                    </Stack>
                    {longtermFeeDcAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        첫달 수수료할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                      </Typography>
                      <Typography variant="body2">
                        - {longtermFeeDcAmount.toLocaleString()}원
                      </Typography>
                    </Stack>
                    )}
                    {longtermDcAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        쿠폰할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                      </Typography>
                      <Typography variant="body2">
                        - {longtermDcAmount.toLocaleString()}원
                      </Typography>
                    </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                      <Typography variant="body2">
                        부가세
                      </Typography>
                      <Typography variant="body2">
                        {longtermFinVat.toLocaleString()}원
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
                </Collapse>
              </CardActionArea>

              {/* collapse button */}
              {!smUp && (
                <Button fullWidth onClick={toggleExpandLongterm} size="small"
                  sx={{ mb: 2, color: "text.disabled" }}
                  endIcon={<Iconify icon={isLongtermExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                >
                  {isLongtermExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                </Button>
              )}
            </Card>
          </Stack>
        </Stack>

        {candidate.status === "crew-negotiate-to-company" && (
        <Fragment>
          {(!smUp || hasContractRange) && (
            <LoadingButton
              type="button"
              loading={isSubmit}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
              onClick={openNegoCommentDialog}
              startIcon={<Iconify icon="eva:alert-circle-fill" />}
              disableRipple
            >
              계약 조건을 수정하고 싶으신가요?
            </LoadingButton>
          )}

          {hasContractRange && (
            <Fragment>
              <Divider sx={{ my: 3 }} />
              <Stack direction="row" alignItems="center">
                <Typography variant="h6">
                  계약 기간
                </Typography>
                <LoadingButton
                  disableRipple
                  variant="text"
                  size="small"
                  onClick={openContractDateRangeDialog}
                  sx={{ color: (theme) => theme.palette.grey[500], fontWeight: (theme) => theme.typography.fontWeightBold }}
                >
                  수정하기
                </LoadingButton>
              </Stack>
              <Stack spacing={.5} sx={{ mt: 1 }}>
                <Stack sx={{ pl: 1 }} direction={{ xs: "column", sm: "row" }} spacing={smUp ? 1 : 0}>
                  <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: (plan === "4weeks" || plan4weeksSelected) ? "secondary.main" : "text.disabled" }}>
                    4주 계약
                  </Typography>
                  <Typography variant={smUp ? "body1" : "body2"} sx={{ color: (plan === "4weeks" || plan4weeksSelected) ? "text.primary" : "text.disabled" }}>
                    {fKrDateKST(contractDateRangeValue[0])} ~ {fKrDateKST(contractDateRangeValue[1])}
                  </Typography>
                </Stack>
                {!isResidentWork ? (
                <Stack sx={{ pl: 1 }} direction={{ xs: "column", sm: "row" }} spacing={smUp ? 1 : 0}>
                  <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: (plan === "trial" || planTrialSelected) ? "secondary.main" : "text.disabled" }}>
                    2주 체험판
                  </Typography>
                  <Typography variant={smUp ? "body1" : "body2"} sx={{ color: (plan === "trial" || planTrialSelected) ? "text.primary" : "text.disabled" }}>
                    {fKrDateKST(contractTrialDateRangeValue[0])} ~ {fKrDateKST(contractTrialDateRangeValue[1])}
                  </Typography>
                </Stack>
                ) : null}
                <Stack sx={{ pl: 1 }} direction={{ xs: "column", sm: "row" }} spacing={smUp ? 1 : 0}>
                  <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: (plan === "longterm" || planLongtermSelected) ? "secondary.main" : "text.disabled" }}>
                    8주 장기
                  </Typography>
                  <Typography variant={smUp ? "body1" : "body2"} sx={{ color: (plan === "longterm" || planLongtermSelected) ? "text.primary" : "text.disabled" }}>
                    {fKrDateKST(contractLongtermDateRangeValue[0])} ~ {fKrDateKST(contractLongtermDateRangeValue[1])}
                  </Typography>
                </Stack>
              </Stack>
            </Fragment>
          )}
          {candidate.status === "crew-negotiate-to-company" && (
          <InfoStyle sx={{ my: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center">
            <Typography variant="body2">
              입금이 확인되면 회사와 크루의 연락처를 서로 전달해 드립니다.
            </Typography>
            </Stack>
            <Typography variant="body2">
              업무 시작일 전까지 크루에게 업무 툴 초대 이메일을 보내주세요. 미발송으로 인한 업무 지연은 기업의 책임입니다.
            </Typography>
          </InfoStyle>
          )}
          {smUp && (
          <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
            {hasContractRange
            ?
            <div />
            :
            <LoadingButton
              type="button"
              loading={isSubmit}
              sx={{
                color: (theme) => theme.palette.grey[500]
              }}
              onClick={openNegoCommentDialog}
              startIcon={<Iconify icon="eva:alert-circle-fill" />}
            >
              계약 조건을 수정하고 싶으신가요?
            </LoadingButton>
            }
            <Stack direction="row">
              <LoadingButton
                type="button"
                loading={isSubmit}
                sx={{ mr: 3 }}
                onClick={() => setSuggestFeedback(true)}
              >
                계약하지 않겠습니다
              </LoadingButton>
              {hasContractRange
              ?
              <LoadingButton
                type="button"
                variant="contained"
                loading={isSubmit}
                onClick={openNegoAcceptDialog}
              >
                이 조건으로 계약 진행할게요
              </LoadingButton>
              :
              <LoadingButton
                type="button"
                variant="contained"
                color="secondary"
                loading={isSubmit}
                onClick={openContractDateRangeDialog}
              >
                시작일 선택하고 계약할게요
              </LoadingButton>
              }
            </Stack>
          </Stack>
          )}
          {!smUp && (
          <Stack spacing={1} alignItems="center" sx={{ mt: 4 }}>
            {hasContractRange
            ?
            <LoadingButton
              fullWidth
              type="button"
              variant="contained"
              loading={isSubmit}
              onClick={openNegoAcceptDialog}
            >
              이 조건으로 계약 진행할게요
            </LoadingButton>
            :
            <LoadingButton
              fullWidth
              type="button"
              variant="contained"
              color="secondary"
              loading={isSubmit}
              onClick={openContractDateRangeDialog}
            >
              시작일 선택하고 계약할게요
            </LoadingButton>
            }
            <LoadingButton
              fullWidth
              type="button"
              loading={isSubmit}
              onClick={() => setSuggestFeedback(true)}
            >
              계약하지 않겠습니다
            </LoadingButton>
          </Stack>
          )}
        </Fragment>
        )}
        {candidate.status === "company-negotiate-rewrite" && (
        <FormStyle>
          <Typography>
            크루에게 계약조건 수정을 요청하였습니다. 다시 제안이 오면 알려드릴게요!
          </Typography>
        </FormStyle>
        )}
        {candidate.status === "accept" && (
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "secondary.main" }} />
              <Typography variant="h6">
                계약 기간
              </Typography>
            </Stack>
            <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
              {fKrDateAbsoulte(candidate.confirmed_start_date)} ~ {fKrDateAbsoulte(candidate.confirmed_end_date)}
            </Typography>
          </Stack>
        )}

      </CardContent>
    </Card>

    <Dialog open={writeNegoComment} onClose={closeNegoCommentDialog} fullWidth maxWidth="xs">
      <DialogTitle sx={{ mb: 1 }}>
        계약 조건을 수정하고 싶으신가요?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <DialogContentText>
            수정하고 싶은 내용을 적어주세요. <br />
            크루가 내용을 확인하고, 계약조건을 수정해서 제안을 다시 보낼거에요.
          </DialogContentText>
          <TextField
            label="수정 요청 내용"
            multiline
            minRows={2}
            value={negoComment}
            error={negoCommentError}
            helperText={negoCommentError ? '내용을 입력해주세요' : ''}
            onChange={handleNegoCommentChange}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          loading={isSubmit}
          onClick={closeNegoCommentDialog}
        >
          닫기
        </LoadingButton>
        <LoadingButton
          variant="contained"
          loading={isSubmit}
          onClick={negoFeedback}
        >
          수정 요청하기
        </LoadingButton>
      </DialogActions>

    </Dialog>

    <FormProvider methods={methods}>
      <RejectCrewDialog
        isOpen={bizRejectDialog}
        closeDialog={closeBizRejectDialog}
        submit={rejectOffer}
        isSubmit={isSubmit}
        isContract
      />
    </FormProvider>

    <Dialog open={acceptNego} onClose={closeNegoAcceptDialog} fullWidth maxWidth="xs">
      <DialogTitle>
        계약을 진행할까요?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <DIOText /> 매니저가 해당 조건으로 크루와 계약을 진행할게요.
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          loading={isSubmit}
          onClick={closeNegoAcceptDialog}
        >
          취소
        </LoadingButton>
        <LoadingButton
          variant="contained"
          loading={isSubmit}
          onClick={negoConfirm}
        >
          계약하기
        </LoadingButton>
      </DialogActions>

    </Dialog>

    <ContractDateRangeDialog
      isOpen={contractDateRange}
      initialValue={contractDateRangeValue}
      closeDialog={(v, w, x) => closeContractDateRangeDialog(v, w, x)}
      minStartDate={candidate.min_start_date}
      plan={plan}
    />

    <NegotiateSuggestAmendBeforeRejectDialog 
      isOpen={suggestFeedback}
      closeDialog={() => setSuggestFeedback(false)}
      goReject={() => openBizRejectDialog()}
      goComment={() => openNegoCommentDialog()}
    />

    <ContractInfoDialog
      isOpen={contractInfoDialog}
      closeDialog={() => closeContractInfoDialog()}
      goAccept={() => updateAndAcceptNego()}
    />

    <InvitationNudgeSnackbar
      open={isOpenNudge}
      close={closeNudge}
    />

    <ContractDoneDialog
      open={contractDoneDialog}
      closeDialog={closeContractDoneDialog}
      depositGuide={depositGuide}
    />
    <NegoSelectPlanWarnDialog
      isOpen={isOpenPlanWarnDialog}
      closeDialog={() => setIsOpenPlanWarnDialog(false)}
    />

    </>
  );
};
